import * as React from 'react';
import { Link } from 'react-router-dom';

import { useCan } from '@/domains/auth';
import type { IFieldHistoryBatch } from '../api/internal-field-contracts';
import { batchLabel } from '../lists';
import { importPath } from '../paths';

/**
 * The batch a history row was written in (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §7) — "Saved from a list", "A mass
 * update", "Imported from a file", "The reversal of an import", with the batch's note beside it —
 * drawn under the row's *What* on a staff field's, a figure's and a placement's history alike,
 * through this one function (`timeline-values.tsx`' precedent).
 *
 * **What the wire does not carry is not said.** The design's "with 11 other cells" and the file's
 * name ("imported from *Merrill_BIS2027_returned.xlsx*") are on no history row
 * (`FieldHistoryBatchDto` is the id, kind, note and import), and a read per row to find them would
 * multiply a popover's one read by its page — so an import's row links to the import instead, where
 * the file is named: "Open the import", for a reader holding `firm_exchange.manage`, the policy of
 * the route it opens; any other reader is told the words alone. A row with no batch draws its
 * *What* as before.
 */
export function withBatch(
  what: React.ReactNode,
  batch: Nullable<IFieldHistoryBatch> | undefined
): React.ReactNode {
  if (batch == null) return what;
  return (
    <>
      {what}
      <HistoryBatch batch={batch} />
    </>
  );
}

function HistoryBatch({ batch }: { batch: IFieldHistoryBatch }) {
  const mayOpen = useCan()('firm_exchange.manage');
  return (
    <span className="block text-xs text-muted-foreground" data-batch={batch.kind}>
      {batchLabel(batch.kind)}
      {batch.note !== null && batch.note.trim() !== '' && <> — “{batch.note}”</>}
      {batch.importId !== null && mayOpen && (
        <>
          {' · '}
          <Link className="underline" to={importPath(batch.importId)}>
            Open the import
          </Link>
        </>
      )}
    </span>
  );
}
